/**
 * useCsvImport — the state machine behind ImportWizard.
 *
 * Steps: upload → map columns → check rows (validate every row) → review (server dry run) → done.
 * Everything async is cancellable and every failure lands in state as a message with a retry path:
 * an error is never rendered as an empty result.
 *
 * @layer widget
 * @package @plerity/dashboard-shared
 */

import {
  type ColumnMapping,
  CsvParseError,
  type CsvRow,
  confirmAll,
  confirmColumn,
  inferHeaders,
  mapColumns,
  missingRequired,
  parseFile,
  pendingConfirmations,
  previewFile,
  setColumnField,
  type ValidatedRow,
  validateRowsAsync,
} from "@plerity/csv";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  BatchTotals,
  ImportCommitOptions,
  ImportDuplicateMatch,
  ImportRowDecision,
  ImportStep,
  ImportWizardState,
  ImportWizardTarget,
  ServerRowIssue,
  UseCsvImportOptions,
} from "./ImportWizard.types";

export const DEFAULT_BATCH_SIZE = 500;
export const DEFAULT_MAX_ROWS = 20_000;
export const DEFAULT_MAX_FILE_BYTES = 100 * 1024 * 1024;
/** Records read for the mapping step: the header plus a few sample rows. */
const PREVIEW_ROWS = 6;

const EMPTY_TOTALS: BatchTotals = { created: 0, updated: 0, skipped: 0, errors: [] };

class LimitError extends Error {}

/** Decisions a duplicate row may take: the match's own list, else update/create/skip for a saved record. */
export function allowedDecisions(match: ImportDuplicateMatch): ReadonlyArray<ImportRowDecision> {
  if (match.allowed && match.allowed.length > 0) return match.allowed;
  return match.source === "existing" ? ["update", "create", "skip"] : ["create", "skip"];
}

/** The decision a duplicate row starts with. Falls back to `skip` if the preferred one is not allowed. */
export function initialDecision(match: ImportDuplicateMatch, fallback: ImportRowDecision = "skip"): ImportRowDecision {
  const allowed = allowedDecisions(match);
  const wanted = match.defaultDecision ?? fallback;
  if (allowed.includes(wanted)) return wanted;
  return allowed.includes("skip") ? "skip" : (allowed[0] as ImportRowDecision);
}

/** One entry per row; a later match for the same row replaces an earlier one. */
function mergeDuplicates(...lists: ReadonlyArray<ReadonlyArray<ImportDuplicateMatch>>): ImportDuplicateMatch[] {
  const byRow = new Map<number, ImportDuplicateMatch>();
  for (const list of lists) for (const match of list) byRow.set(match.row, match);
  return [...byRow.values()].sort((a, b) => a.row - b.row);
}

const EMPTY_UPSERT = (): ImportWizardState<unknown>["upsert"] => ({ duplicates: [], decisions: {} });

function initialState<T>(): ImportWizardState<T> {
  return {
    step: "upload",
    file: null,
    upload: { status: "idle", message: null },
    preview: null,
    hasHeader: true,
    headers: [],
    renamed: [],
    mapping: [],
    validate: { status: "idle", progress: 0, message: null, result: null },
    skipInvalid: false,
    dryRun: { status: "idle", done: 0, total: 0, message: null, totals: null },
    commit: { status: "idle", done: 0, total: 0, message: null, totals: EMPTY_TOTALS, nextBatch: 0 },
    upsert: EMPTY_UPSERT(),
  };
}

function errorDetail(error: unknown): string {
  return error instanceof Error ? error.message : "";
}

function describeError(error: unknown, fallback: string): string {
  if (error instanceof CsvParseError || error instanceof LimitError) return error.message;
  const detail = errorDetail(error);
  return detail.length > 0 ? `${fallback} (${detail})` : fallback;
}

function addTotals(base: BatchTotals, next: BatchTotals): BatchTotals {
  return {
    created: base.created + next.created,
    updated: base.updated + next.updated,
    skipped: base.skipped + next.skipped,
    errors: [...base.errors, ...next.errors],
  };
}

function chunked<T>(items: readonly T[], size: number): T[][] {
  const width = Number.isFinite(size) && size > 0 ? Math.floor(size) : Math.max(items.length, 1);
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += width) out.push(items.slice(i, i + width));
  return out;
}

async function* dataRows(source: AsyncIterable<CsvRow>, skipFirst: boolean, maxRows: number): AsyncGenerator<CsvRow> {
  let seen = 0;
  let first = true;
  for await (const row of source) {
    if (first && skipFirst) {
      first = false;
      continue;
    }
    first = false;
    seen += 1;
    if (seen > maxRows) {
      throw new LimitError(
        `This file has more than ${maxRows.toLocaleString("en-US")} rows. Split it into smaller files and import them one at a time.`
      );
    }
    yield row;
  }
}

export function useCsvImport<T>(target: ImportWizardTarget<T>, options: UseCsvImportOptions = {}) {
  const {
    batchSize = DEFAULT_BATCH_SIZE,
    maxRows = DEFAULT_MAX_ROWS,
    maxFileBytes = DEFAULT_MAX_FILE_BYTES,
    allowSkipInvalid = true,
    formulaPolicy = "flag",
    onComplete,
  } = options;

  const [state, setState] = useState<ImportWizardState<T>>(() => initialState<T>());
  const stateRef = useRef(state);
  stateRef.current = state;

  const targetRef = useRef(target);
  targetRef.current = target;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const patch = useCallback((next: Partial<ImportWizardState<T>>) => {
    if (!mountedRef.current) return;
    setState((current) => ({ ...current, ...next }));
  }, []);

  const goTo = useCallback((step: ImportStep) => patch({ step }), [patch]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (mountedRef.current) setState(initialState<T>());
  }, []);

  // ---------------------------------------------------------------- step 1: upload

  const selectFile = useCallback(
    async (file: File) => {
      abortRef.current?.abort();
      if (file.size > maxFileBytes) {
        patch({
          file: null,
          upload: {
            status: "error",
            message: `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is ${(maxFileBytes / 1024 / 1024).toFixed(0)} MB. Split it into smaller files.`,
          },
        });
        return;
      }
      patch({ file, upload: { status: "reading", message: null } });
      try {
        const preview = await previewFile(file, { maxRows: PREVIEW_ROWS });
        if (preview.rows.length === 0) {
          patch({
            file: null,
            upload: { status: "error", message: "That file is empty. Choose a file that has at least a header row." },
          });
          return;
        }
        const rows = preview.rows.map((row) => row.cells);
        const inferred = inferHeaders(rows[0] as string[]);
        patch({
          file,
          upload: { status: "idle", message: null },
          preview: {
            rows,
            delimiter: preview.meta.delimiter,
            encoding: preview.meta.encoding,
            hadBom: preview.meta.hadBom,
          },
          hasHeader: inferred.hasHeader,
          headers: inferred.headers,
          renamed: inferred.renamed,
          mapping: mapColumns(inferred.headers, targetRef.current.fields),
          validate: { status: "idle", progress: 0, message: null, result: null },
          dryRun: { status: "idle", done: 0, total: 0, message: null, totals: null },
          upsert: EMPTY_UPSERT(),
          skipInvalid: false,
          step: "map",
        });
      } catch (error) {
        patch({
          file: null,
          upload: { status: "error", message: describeError(error, "That file could not be read as a CSV.") },
        });
      }
    },
    [maxFileBytes, patch]
  );

  // ---------------------------------------------------------------- step 2: map columns

  const setHasHeader = useCallback(
    (hasHeader: boolean) => {
      const { preview } = stateRef.current;
      if (!preview || preview.rows.length === 0) return;
      const inferred = inferHeaders(preview.rows[0] as string[], { hasHeader });
      patch({
        hasHeader,
        headers: inferred.headers,
        renamed: inferred.renamed,
        mapping: mapColumns(inferred.headers, targetRef.current.fields),
        validate: { status: "idle", progress: 0, message: null, result: null },
      });
    },
    [patch]
  );

  const setField = useCallback(
    (column: number, field: string | null) =>
      patch({ mapping: setColumnField(stateRef.current.mapping, column, field) }),
    [patch]
  );
  const confirm = useCallback(
    (column: number) => patch({ mapping: confirmColumn(stateRef.current.mapping, column) }),
    [patch]
  );
  const confirmEvery = useCallback(() => patch({ mapping: confirmAll(stateRef.current.mapping) }), [patch]);

  const missing = useMemo(() => missingRequired(state.mapping, target.fields), [state.mapping, target.fields]);
  const pending = useMemo(() => pendingConfirmations(state.mapping), [state.mapping]);
  const anyMapped = state.mapping.some((entry) => entry.field !== null);
  const canCheckRows = missing.length === 0 && pending.length === 0 && anyMapped;

  // ---------------------------------------------------------------- step 3: validate every row

  const validate = useCallback(async () => {
    const current = stateRef.current;
    if (!current.file) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const file = current.file;
    const mapping: ColumnMapping = current.mapping;
    patch({
      step: "validate",
      validate: { status: "running", progress: 0, message: null, result: null },
      dryRun: { status: "idle", done: 0, total: 0, message: null, totals: null },
      upsert: EMPTY_UPSERT(),
      skipInvalid: false,
    });

    let lastPercent = -1;
    try {
      const handle = parseFile(file, {
        signal: controller.signal,
        onProgress: (bytes) => {
          const percent = file.size > 0 ? Math.min(99, Math.floor((bytes / file.size) * 100)) : 0;
          if (percent !== lastPercent) {
            lastPercent = percent;
            setState((s) => (mountedRef.current ? { ...s, validate: { ...s.validate, progress: percent / 100 } } : s));
          }
        },
      });
      const result = await validateRowsAsync(
        dataRows(handle, current.hasHeader, maxRows),
        targetRef.current.schema,
        mapping,
        { formulaPolicy }
      );
      if (controller.signal.aborted) return;
      patch({ validate: { status: "done", progress: 1, message: null, result } });
    } catch (error) {
      if (controller.signal.aborted || (error instanceof Error && error.name === "AbortError")) return;
      patch({
        validate: {
          status: "error",
          progress: 0,
          message: describeError(error, "The file could not be checked."),
          result: null,
        },
      });
    }
  }, [formulaPolicy, maxRows, patch]);

  const cancelValidate = useCallback(() => {
    abortRef.current?.abort();
    patch({ step: "map", validate: { status: "idle", progress: 0, message: null, result: null } });
  }, [patch]);

  // ---------------------------------------------------------------- step 4: server dry run

  const runBatches = useCallback(
    async (
      rows: ReadonlyArray<ValidatedRow<T>>,
      dryRun: boolean,
      startBatch: number,
      startTotals: BatchTotals,
      onProgress: (done: number, total: number, nextBatch: number, totals: BatchTotals) => void,
      /** Upsert only: the user's decisions, sent with the real commit. */
      decisions?: Readonly<Record<number, ImportRowDecision>>
    ): Promise<
      | { ok: true; totals: BatchTotals; duplicates: ImportDuplicateMatch[] }
      | { ok: false; error: unknown; nextBatch: number; totals: BatchTotals }
    > => {
      const batches = chunked(rows, batchSize);
      const collectDuplicates = dryRun && targetRef.current.upsert !== undefined;
      const duplicates: ImportDuplicateMatch[] = [];
      let totals = startTotals;
      let done = batches.slice(0, startBatch).reduce((sum, batch) => sum + batch.length, 0);
      for (let i = startBatch; i < batches.length; i++) {
        const batch = batches[i] as ValidatedRow<T>[];
        try {
          // Default-off contract: without `upsert` the options object is exactly `{ dryRun }`.
          let options: ImportCommitOptions = { dryRun };
          if (decisions !== undefined) {
            const batchDecisions: Record<number, ImportRowDecision> = {};
            for (const row of batch) {
              const decision = decisions[row.index];
              if (decision !== undefined) batchDecisions[row.index] = decision;
            }
            options = { dryRun, decisions: batchDecisions };
          }
          const response = await targetRef.current.commit(batch, options);
          if (collectDuplicates && response.duplicates) duplicates.push(...response.duplicates);
          totals = addTotals(totals, {
            created: response.created,
            updated: response.updated ?? 0,
            skipped: response.skipped ?? 0,
            errors: (response.errors ?? []).map(
              (e): ServerRowIssue => ({ row: e.row, field: e.field ?? null, message: e.message })
            ),
          });
        } catch (error) {
          return { ok: false, error, nextBatch: i, totals };
        }
        done += batch.length;
        onProgress(done, rows.length, i + 1, totals);
      }
      return { ok: true, totals, duplicates };
    },
    [batchSize]
  );

  const runDryRun = useCallback(async () => {
    const result = stateRef.current.validate.result;
    if (!result || result.rows.length === 0) return;
    patch({
      step: "review",
      skipInvalid: false,
      dryRun: { status: "running", done: 0, total: result.rows.length, message: null, totals: null },
      upsert: EMPTY_UPSERT(),
    });
    const outcome = await runBatches(result.rows, true, 0, EMPTY_TOTALS, (done, total, _next, totals) =>
      patch({ dryRun: { status: "running", done, total, message: null, totals } })
    );
    const failDryRun = (error: unknown) =>
      patch({
        dryRun: {
          status: "error",
          done: 0,
          total: result.rows.length,
          message: describeError(
            error,
            `The ${targetRef.current.entityName} could not be checked with the server. Nothing was imported.`
          ),
          totals: null,
        },
      });
    if (!outcome.ok) {
      failDryRun(outcome.error);
      return;
    }

    const upsert = targetRef.current.upsert;
    if (upsert) {
      // Duplicates only matter for rows that will actually be sent (not ones the dry run refused).
      const refused = new Set(outcome.totals.errors.map((e) => e.row));
      const candidates = result.rows.filter((row) => !refused.has(row.index));
      let found: ReadonlyArray<ImportDuplicateMatch> = [];
      if (upsert.findDuplicates && candidates.length > 0) {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        try {
          found = await upsert.findDuplicates(candidates, { signal: controller.signal });
        } catch (error) {
          if (controller.signal.aborted || (error instanceof Error && error.name === "AbortError")) return;
          failDryRun(error);
          return;
        }
        if (controller.signal.aborted) return;
      }
      const candidateRows = new Set(candidates.map((row) => row.index));
      const duplicates = mergeDuplicates(outcome.duplicates, found).filter((m) => candidateRows.has(m.row));
      const decisions: Record<number, ImportRowDecision> = {};
      for (const match of duplicates) decisions[match.row] = initialDecision(match, upsert.defaultDecision);
      patch({ upsert: { duplicates, decisions } });
    }

    patch({
      dryRun: {
        status: "done",
        done: result.rows.length,
        total: result.rows.length,
        message: null,
        totals: outcome.totals,
      },
    });
  }, [patch, runBatches]);

  const setSkipInvalid = useCallback((skipInvalid: boolean) => patch({ skipInvalid }), [patch]);

  // ---------------------------------------------------------------- upsert decisions (target opt-in)

  const upsertEnabled = target.upsert !== undefined;

  /** Set one duplicate row's decision. Ignored for a row with no match or a decision it does not allow. */
  const setDecision = useCallback(
    (row: number, decision: ImportRowDecision) => {
      const { duplicates, decisions } = stateRef.current.upsert;
      const match = duplicates.find((m) => m.row === row);
      if (!match || !allowedDecisions(match).includes(decision)) return;
      patch({ upsert: { duplicates, decisions: { ...decisions, [row]: decision } } });
    },
    [patch]
  );

  /** Apply one decision to every duplicate row that allows it; rows that do not allow it keep theirs. */
  const setAllDecisions = useCallback(
    (decision: ImportRowDecision) => {
      const { duplicates, decisions } = stateRef.current.upsert;
      const next = { ...decisions };
      for (const match of duplicates) if (allowedDecisions(match).includes(decision)) next[match.row] = decision;
      patch({ upsert: { duplicates, decisions: next } });
    },
    [patch]
  );

  // ---------------------------------------------------------------- derived numbers for review / commit

  const invalidRows = state.validate.result?.errorCount ?? 0;
  const rejectedRows = useMemo(() => {
    const errors = state.dryRun.totals?.errors ?? [];
    return new Set(errors.map((e) => e.row));
  }, [state.dryRun.totals]);
  const importableRows = useMemo(
    () => (state.validate.result?.rows ?? []).filter((row) => !rejectedRows.has(row.index)),
    [state.validate.result, rejectedRows]
  );
  const problemCount = invalidRows + rejectedRows.size;
  /** Upsert only: how many duplicate rows the user chose to create / update / skip. All zero when off. */
  const decisionCounts = useMemo(() => {
    const counts: Record<ImportRowDecision, number> = { create: 0, update: 0, skip: 0 };
    if (!upsertEnabled) return counts;
    for (const match of state.upsert.duplicates) {
      const decision = state.upsert.decisions[match.row];
      if (decision !== undefined) counts[decision] += 1;
    }
    return counts;
  }, [upsertEnabled, state.upsert]);
  /** Rows that will be written (created or updated). Equals `importableRows.length` unless upsert skips some. */
  const writeCount = importableRows.length - decisionCounts.skip;
  /** Rows can be committed once the dry run passed, and any problem rows are explicitly accepted as skipped. */
  const canCommit =
    state.dryRun.status === "done" && writeCount > 0 && (problemCount === 0 || (allowSkipInvalid && state.skipInvalid));

  // ---------------------------------------------------------------- step 4→5: commit

  const finish = useCallback(
    (totals: BatchTotals) => {
      const fileName = stateRef.current.file?.name ?? "";
      patch({
        step: "done",
        commit: {
          status: "done",
          done: stateRef.current.commit.total,
          total: stateRef.current.commit.total,
          message: null,
          totals,
          nextBatch: 0,
        },
      });
      onCompleteRef.current?.({
        fileName,
        created: totals.created,
        updated: totals.updated,
        skipped: totals.skipped,
        invalidRows,
        rejectedRows: rejectedRows.size,
      });
    },
    [invalidRows, patch, rejectedRows]
  );

  const runCommit = useCallback(async () => {
    const rows = importableRows;
    if (rows.length === 0) return;
    const resume = stateRef.current.commit.status === "error";
    const startBatch = resume ? stateRef.current.commit.nextBatch : 0;
    const startTotals = resume ? stateRef.current.commit.totals : EMPTY_TOTALS;

    patch({
      commit: {
        status: "running",
        done: stateRef.current.commit.done,
        total: rows.length,
        message: null,
        totals: startTotals,
        nextBatch: startBatch,
      },
    });
    const outcome = await runBatches(
      rows,
      false,
      startBatch,
      startTotals,
      (done, total, nextBatch, totals) =>
        patch({ commit: { status: "running", done, total, message: null, totals, nextBatch } }),
      targetRef.current.upsert ? stateRef.current.upsert.decisions : undefined
    );
    if (outcome.ok) {
      finish(outcome.totals);
      return;
    }
    const alreadyImported = chunked(rows, batchSize)
      .slice(0, outcome.nextBatch)
      .reduce((sum, batch) => sum + batch.length, 0);
    const detail = errorDetail(outcome.error);
    const saved = alreadyImported.toLocaleString("en-US");
    patch({
      commit: {
        status: "error",
        done: alreadyImported,
        total: rows.length,
        totals: outcome.totals,
        nextBatch: outcome.nextBatch,
        message:
          alreadyImported > 0
            ? `The import stopped after ${saved} of ${rows.length.toLocaleString("en-US")} rows. Those ${saved} are already saved. Press Retry to continue with the rest. Do not upload the file again.${detail ? ` Details: ${detail}` : ""}`
            : describeError(outcome.error, `The import failed. No ${targetRef.current.entityName} were saved.`),
      },
    });
  }, [batchSize, finish, importableRows, patch, runBatches]);

  const back = useCallback(() => {
    abortRef.current?.abort();
    const { step } = stateRef.current;
    if (step === "map") reset();
    else if (step === "validate") patch({ step: "map" });
    else if (step === "review") patch({ step: "validate", skipInvalid: false });
  }, [patch, reset]);

  return {
    state,
    // upload
    selectFile,
    // map
    setHasHeader,
    setField,
    confirm,
    confirmAll: confirmEvery,
    missingRequired: missing,
    pendingConfirmations: pending,
    canCheckRows,
    // validate
    validate,
    cancelValidate,
    // review
    runDryRun,
    setSkipInvalid,
    invalidRows,
    rejectedRows,
    importableRows,
    problemCount,
    canCommit,
    allowSkipInvalid,
    // upsert (target opt-in; inert when `target.upsert` is absent)
    upsertEnabled,
    setDecision,
    setAllDecisions,
    decisionCounts,
    writeCount,
    // commit
    runCommit,
    // navigation
    goTo,
    back,
    reset,
  };
}

export type CsvImportController<T> = ReturnType<typeof useCsvImport<T>>;
